/**
 * meterGeometry — DPR-aware canvas sizing + ResizeObserver wiring.
 *
 * Pure-function workshop per A6 §useMeterRenderer (Option A): the
 * closure-bound bag of canvas + config + rings + dial state lives in
 * `useMeterRenderer.ts` (the rAF shell). This module exposes two
 * functions that operate on that bag:
 *
 * - `recomputeGeometry` reads the current bounding rect + DPR, sizes
 *   the canvas backing store, rebuilds `MeterConfig`, and creates a
 *   fresh `RingSet` + `DialState` via the canvas/meter workshops.
 *   Returns `true` if the recompute landed with a non-zero rect — the
 *   back-nav zero-rect case returns `false` so the caller can keep
 *   the ResizeObserver primed for recovery.
 *
 * - `watchMeterResize` wraps glass-ui's `useResizeObserver` so the
 *   geometry rebuilds when `.meter-col` lifts back into layout. The
 *   on-resize callback re-paints whatever state the engine is in;
 *   the choice of recovery paint lives in the shell.
 *
 * The setup-time composable that mints the reactive token-color refs
 * (`--meter-track-stroke`, `--meter-dial-color`) lives in the sibling
 * `useMeterTokenColors.ts` — split out so this file is honestly pure
 * per FA-2 §3.2 (AH.W5-b.2c).
 */

import { type Ref } from "vue";
import { useResizeObserver } from "@mkbabb/glass-ui/dom";
import { Canvas } from "../../ui/meter/canvas/core.ts";
import {
    computeMeterConfig,
    type MeterConfig,
} from "../../ui/meter/canvas/meter/config.ts";
import {
    createRings,
    type RingSet,
} from "../../ui/meter/canvas/meter/rings.ts";
import {
    createDial,
    type DialState,
} from "../../ui/meter/canvas/meter/dial.ts";
import type { MeterTokenColors } from "./useMeterTokenColors.ts";

/**
 * The mutable geometry bundle owned by the renderer shell. The shell
 * passes this into the pure-function workshop calls below; this module
 * never holds the bag itself.
 */
export interface MeterGeometryBag {
    canvas: Canvas | null;
    config: MeterConfig | null;
    rings: RingSet | null;
    dial: DialState | null;
}

/**
 * Recompute the canvas backing-store geometry from the current bounding
 * rect + DPR. Pure function: mutates the `bag` in place; returns `true`
 * if the recompute succeeded with a non-zero rect.
 *
 * Zero-rect case: the canvas can mount inside a `.meter-col` that's
 * collapsed by stale `data-complete="true"`. Returning `false` lets
 * the shell install the ResizeObserver regardless, so the eventual
 * non-zero rect drives a real recompute + paint when the cell recovers.
 */
export function recomputeGeometry(
    meterRef: Ref<HTMLCanvasElement | null>,
    bag: MeterGeometryBag,
    tokens: MeterTokenColors,
): boolean {
    const el = meterRef.value;
    if (!el) return false;
    const ctx = el.getContext("2d");
    if (!ctx) return false;

    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    const dpr = window.devicePixelRatio || 1;

    const config = computeMeterConfig(rect.width, rect.height, dpr);
    el.width = config.canvasWidth;
    el.height = config.canvasHeight;

    const canvas = new Canvas(el, ctx, [config.originX, config.originY]);

    // `useTokenColor` returns a controls bag whose `value` field is itself
    // a Ref<string>; unwrap to get the resolved CSS value after `refresh()`.
    tokens.track.refresh();
    tokens.dial.refresh();
    const bgColor = tokens.track.value.value;
    const dialColor = tokens.dial.value.value;

    // DESIGN-INTAKE-2 line-54 — resolve the active colour scheme so the ring
    // ladder's dark rung composes for the dark ground (lightened, not washed
    // to black). The canonical dark signal is the `.dark` class on the
    // document root (glass-ui's `useGlobalDark` installs it; the dashboard's
    // `.dark` token block keys off it). Reading it HERE — inside the same
    // recompute the dark toggle already forces via `installDarkModeSync`'s
    // dispose→initialize — keeps the ladder synchronous + allocation-free and
    // tracks the live scheme without a separate reactive subscription. The
    // resolved `--meter-track-stroke`/`--meter-dial-color` tokens above are
    // already scheme-aware via the same `.dark` cascade; this extends that
    // scheme-awareness to the phase ladder.
    const isDark =
        typeof document !== "undefined" &&
        document.documentElement.classList.contains("dark");

    bag.canvas = canvas;
    bag.config = config;
    bag.rings = createRings(config, bgColor, el, isDark);
    bag.dial = createDial(config, dialColor);
    return true;
}

/**
 * Install a ResizeObserver on the canvas so geometry rebuilds when
 * `.meter-col` lifts back into layout. Idempotent — the shell tracks
 * `installed` and only invokes this once per mount.
 *
 * The callback receives no args; the caller resolves the post-resize
 * paint path (idle oscillator, running tick, completed hold) from its
 * own closure state.
 */
export function watchMeterResize(
    meterRef: Ref<HTMLCanvasElement | null>,
    onResize: () => void,
): void {
    useResizeObserver(meterRef, () => {
        if (!meterRef.value) return;
        onResize();
    });
}
